import { User } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPlayers, Player } from "../../api/players";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import CustomSelect from "../CustomSelect";
import { inputStyle, smallBtn } from "../../styles";

const PAGE_SIZE = 10;
const POSITIONS = ["", "GK", "DF", "MF", "FW"] as const;

const pillBtn = (active: boolean): React.CSSProperties => ({
  padding: "3px 10px", borderRadius: 99, fontSize: "0.72rem", fontWeight: 600,
  cursor: "pointer", fontFamily: "var(--font-body)",
  border: active ? "1px solid var(--color-accent)" : "1px solid var(--color-border)",
  background: active ? "var(--color-accent-glow)" : "transparent",
  color: active ? "var(--color-accent)" : "var(--color-text-muted)",
});

interface Props {
  user: User;
  competitionId: string;
  clubs: string[];
  excludePlayerIds: string[];
  isMobile: boolean;
  onSelectPlayer: (player: Player) => void;
}

/** Always-browsable, paginated player picker for a Syndicate player pot.
 * Mounted only while its pot is expanded (see SyndicatePage's accordion) —
 * unmounting drops all local filter/page state, so re-expanding starts fresh. */
export default function SyndicatePlayerPicker({ user, competitionId, clubs, excludePlayerIds, isMobile, onSelectPlayer }: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("");
  const [club, setClub] = useState("");
  const [page, setPage] = useState(1);
  const [players, setPlayers] = useState<Player[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const excludeKey = excludePlayerIds.join(",");

  const fetchPage = useCallback(async (searchVal: string, clubVal: string, positionVal: string, pageVal: number) => {
    setLoading(true);
    try {
      const result = await fetchPlayers(user, {
        competitionId,
        page: pageVal,
        pageSize: PAGE_SIZE,
        search: searchVal || undefined,
        club: clubVal || undefined,
        position: positionVal || undefined,
        excludePlayerIds: excludePlayerIds.length > 0 ? excludePlayerIds : undefined,
      });
      setPlayers(result.players);
      setTotal(result.total);
    } catch {
      setPlayers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, competitionId, excludeKey]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      fetchPage(search, club, position, 1);
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, club, position]);

  useEffect(() => {
    fetchPage(search, club, position, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <span style={{ position: "absolute", left: "0.6rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.85rem", color: "var(--color-accent)", pointerEvents: "none" }}>⌕</span>
        <input
          style={{ ...inputStyle, fontSize: "0.85rem", paddingLeft: "1.8rem", width: "100%" }}
          placeholder={t("syndicate.searchPlayerPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>
            {t("syndicate.filterPosition")}
          </span>
          {POSITIONS.map((pos) => (
            <button key={pos || "all"} onClick={() => setPosition(pos)} style={pillBtn(position === pos)}>
              {pos || t("syndicate.filterAll")}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>
            {t("syndicate.filterClub")}
          </span>
          <div style={{ display: "inline-flex", borderRadius: 99, border: "1px solid var(--color-accent)", overflow: "hidden" }}>
            <CustomSelect
              value={club}
              onChange={setClub}
              options={clubs.map((c) => ({ value: c, label: c }))}
              placeholder={t("syndicate.allClubs")}
              style={{ border: "none", fontSize: "0.72rem", padding: "3px 8px", fontWeight: 600, borderRadius: 99 }}
            />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr auto" : "1fr 1fr auto auto", gap: "0.75rem", padding: "0.4rem 0.6rem", borderBottom: "1px solid var(--color-border-subtle)" }}>
        <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>{t("syndicate.colPlayer")}</span>
        {!isMobile && <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>{t("syndicate.colClub")}</span>}
        {!isMobile && <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-dim)" }}>{t("syndicate.colPos")}</span>}
        <span style={{ width: isMobile ? 60 : 72 }} />
      </div>

      <div style={{ opacity: loading ? 0.4 : 1, transition: "opacity 0.15s" }}>
        {players.map((p) => {
          const cfg = getClubBadge(p.club);
          return (
            <div
              key={p.id}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr auto" : "1fr 1fr auto auto",
                gap: "0.75rem",
                alignItems: "center",
                padding: "0.5rem 0.6rem",
                borderBottom: "1px solid var(--color-border-subtle)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </span>
                {isMobile && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginTop: 2 }}>
                    <ClubBadge config={cfg} size={12} title={p.club} />
                    <span style={{ fontSize: "0.68rem", color: "var(--color-text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.club}</span>
                  </div>
                )}
              </div>
              {!isMobile && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", minWidth: 0 }}>
                  <ClubBadge config={cfg} size={16} title={p.club} />
                  <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.club}</span>
                </div>
              )}
              {!isMobile && (
                <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", padding: "2px 5px", borderRadius: 4, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", border: "1px solid var(--color-border-subtle)" }}>
                  {p.position ?? "—"}
                </span>
              )}
              <div style={{ width: isMobile ? 60 : 72, display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => onSelectPlayer(p)}
                  style={{ fontSize: "0.7rem", width: isMobile ? 54 : 68, background: "var(--color-accent)", border: "none", borderRadius: 6, padding: "0.28rem 0.4rem", color: "var(--color-btn-primary-text)", cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: 600 }}
                >
                  {t("syndicate.pick")}
                </button>
              </div>
            </div>
          );
        })}
        {players.length === 0 && !loading && (
          <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
            {t("syndicate.noPlayersFound")}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.6rem" }}>
          <button style={smallBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t("syndicate.previous")}</button>
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", flex: 1, textAlign: "center" }}>
            {t("syndicate.pagination", { page, totalPages })}
          </span>
          <button style={smallBtn} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t("syndicate.next")}</button>
        </div>
      )}
    </div>
  );
}
